import { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import Autocomplete from '@mui/material/Autocomplete';
import MuiTextField from '@mui/material/TextField';
import Button from '../components/Button';
import { getProvinciasCompra, getLocalities } from '../../api/config';
import { getCommunityByLocation } from '../../api/communities';

// Paleta EntradaWeb
const COLORS = {
  bg: '#F8F6FF',
  orange: '#FF6600',
  text: '#221238',
  muted: '#6E6779',
};

const FONT = "'Poppins','Helvetica Neue',Helvetica,sans-serif";

interface ProvinciaOption {
  id: string;
  label: string;
}

const ComunidadesEntradaweb = () => {
  const [provinciasOpciones, setProvinciasOpciones] = useState<ProvinciaOption[]>([]);
  const [municipiosOpciones, setMunicipiosOpciones] = useState<ProvinciaOption[]>([]);
  const [provinciaSeleccionada, setProvinciaSeleccionada] = useState<ProvinciaOption | null>(null);
  const [municipioSeleccionado, setMunicipioSeleccionado] = useState<ProvinciaOption | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [loadingLink, setLoadingLink] = useState<boolean>(false);
  const [sinComunidad, setSinComunidad] = useState<boolean>(false);

  // Carga inicial de provincias
  useEffect(() => {
    (async () => {
      try {
        const response = await getProvinciasCompra();
        const transformed = response.map(({ id, name }: { id: string; name: string }) => ({
          id,
          label: name,
        }));
        setProvinciasOpciones(transformed);
      } catch (e) {
        setProvinciasOpciones([]);
      }
    })();
  }, []);

  const handleProvinciaChange = async (provincia: ProvinciaOption | null) => {
    setProvinciaSeleccionada(provincia);
    // Al cambiar de provincia reseteamos localidad, link y estados
    setMunicipioSeleccionado(null);
    setMunicipiosOpciones([]);
    setLink(null);
    setSinComunidad(false);

    if (!provincia) return;

    try {
      const resp: { id: string; name: string }[] = await getLocalities(provincia.id);
      setMunicipiosOpciones(resp.map(({ id, name }) => ({ id, label: name })));
    } catch (e) {
      setMunicipiosOpciones([]);
    }
  };

  const handleMunicipioChange = async (municipio: ProvinciaOption | null) => {
    setMunicipioSeleccionado(municipio);
    setLink(null);
    setSinComunidad(false);

    if (!municipio) return;

    // Al traer la localidad le pegamos al endpoint /v1/communities?idLocation=
    setLoadingLink(true);
    try {
      const resp = await getCommunityByLocation(municipio.id);
      if (resp && resp.link) {
        setLink(resp.link);
      } else {
        setSinComunidad(true);
      }
    } catch (e) {
      setSinComunidad(true);
    } finally {
      setLoadingLink(false);
    }
  };

  const handleUnirme = () => {
    if (link) {
      window.location.href = link;
    }
  };

  return (
    <div
      style={{
        minHeight: '70vh',
        background: COLORS.bg,
        fontFamily: FONT,
        color: COLORS.text,
      }}
    >
      <style>{`
        .cew-btn.btn,
        .cew-btn.btn:hover,
        .cew-btn.btn:focus,
        .cew-btn.btn:focus-visible,
        .cew-btn.btn:active,
        .cew-btn.btn:first-child:active {
          background-color: ${COLORS.orange} !important;
          border: 0 !important;
          color: #FFFFFF !important;
        }
        .cew-btn-icon { width: 22px; height: 22px; }
        .App_mobile .cew-btn-icon { width: 33px; height: 33px; }
      `}</style>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '48px 20px 64px' }}>
        <header style={{ textAlign: 'center', marginBottom: 8 }}>
          <h1
            style={{
              fontWeight: 800,
              fontSize: 'clamp(20px, 5vw, 30px)',
              lineHeight: 1.3,
              margin: '0 0 12px',
              color: COLORS.text,
            }}
          >
            UNITE A NUESTRA COMUNIDAD DE{' '}
            <span style={{ color: '#25d366' }}>WHATSAPP</span> PARA RECIBIR NOVEDADES Y OFERTAS
            EXCLUSIVAS
          </h1>
          <br></br>
          <p style={{ color: COLORS.muted, fontWeight: 700, fontSize: 15, margin: 0, textAlign: 'center' }}>
            Por favor Selecciona tu Provincia y Localidad/Ciudad.
          </p>
        </header>

        <div
          style={{
            background: '#FFFFFF',
            border: '1.5px solid #EBE8F0',
            borderRadius: 16,
            padding: '24px 20px',
          }}
        >
          <Row>
            <Col xs={12} md={6} className="mb-3 mb-md-0">
              <Autocomplete
                disablePortal
                options={provinciasOpciones as ProvinciaOption[]}
                getOptionLabel={(option) => option.label}
                value={provinciaSeleccionada}
                renderInput={(params) => <MuiTextField {...params} label="Provincia" />}
                onChange={(event, provincia) => handleProvinciaChange(provincia)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Col>
            <Col xs={12} md={6}>
              <Autocomplete
                disablePortal
                options={municipiosOpciones as ProvinciaOption[]}
                getOptionLabel={(option) => option.label}
                value={municipioSeleccionado}
                disabled={!provinciaSeleccionada}
                renderInput={(params) => <MuiTextField {...params} label="Localidad" />}
                onChange={(event, municipio) => handleMunicipioChange(municipio)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Col>
          </Row>

          {sinComunidad && (
            <p style={{ color: COLORS.muted, fontSize: 15.5, margin: '14px 0 0', textAlign: 'center' }}>
              No tenemos registrada tu localidad o ciudad en nuestras comunidades, por favor elige la capital de tu provincia.
            </p>
          )}

          <div className="buy-tickets" style={{ marginTop: 24 }}>
            <Button
              className="cew-btn w-100 fw-bold py-2 fs-5 d-flex align-items-center justify-content-center"
              disabled={!link || loadingLink}
              onClick={handleUnirme}
            >
              <img
                className="cew-btn-icon"
                src="/images/icon-communities-white.png"
                alt=""
                style={{ marginRight: 8 }}
              />
              Unirme a Comunidad
            </Button>
            <p style={{ color: COLORS.muted, fontSize: 15.5, margin: '14px 0 0', textAlign: 'center', lineHeight: 1.0, }}>Haz click en este botón luego de seleccionar tu Provincia y Localidad/Ciudad para ir a WhatsApp y aceptar unirte a nuestra Comunidad.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComunidadesEntradaweb;
